import React, { useEffect, useState } from 'react'
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Chip,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Tooltip,
  Alert,
  Select,
} from '@mui/material'
import {
  Save,
  ArrowBack,
  Add,
  Delete,
  DragIndicator,
  Checklist,
  EventRepeat,
  Shield,
} from '@mui/icons-material'
import API from '../../../services/API'
import Loading from '../../loading/loadingPage'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import ErrorModal from '../../../components/Modals/Error'
import SuccessModal from '../../../components/Modals/Success'
import { useDocumentTitle } from '../../../helpers/DocumentTitleManager'

const categoryOptions = [
  'IPC',
  'Medicines',
  'Safeguarding',
  'Clinical Records',
  'Governance',
  'Fleet & Equipment',
  'Equipment',
  'Workforce',
  'Events',
  'Operations',
  'Administration',
]

const frequencyOptions = [
  { label: 'Ad hoc / Manual', value: '' },
  { label: 'Daily', value: 1 },
  { label: 'Weekly', value: 7 },
  { label: 'Monthly', value: 30 },
  { label: 'Quarterly', value: 90 },
  { label: 'Annually', value: 365 },
]

function useQuery() {
  const { search } = useLocation()
  return React.useMemo(() => new URLSearchParams(search), [search])
}

export default function EditAuditTemplatePage() {
  const [form, setForm] = useState({
    title: '',
    auditDescription: '',
    category: 'IPC',
    requiredFrequency: 30,
    isActive: true,
    isRequired: true,
    isSystemTemplate: true,
    tenantcanDisable: true,
    questions: [
      {
        name: '',
        guidance: '',
        required: true,
      },
    ],
  })
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const { id } = useParams()
  const {setPageTitle} = useDocumentTitle()
  const [uploading, setUploading] = useState(false)
  const navigate = useNavigate()
  let query = useQuery()
  const addedSuccess = query.get('addedSuccess')

  const [searchParams, setSearchParams] = useSearchParams()

  const removeQueryParam = (key) => {
    searchParams.delete(key)
    setSearchParams(searchParams)
  }

  useEffect(() => {
    if (form?.title) {
      setPageTitle(form.title)
    }
  }, [form?.title, setPageTitle])

  const noSubmit =
    !form?.title ||
    !form?.category ||
    !form?.requiredFrequency ||
    form?.questions?.length === 0

  useEffect(() => {
    if (!id) {
      setLoading(false)
      return
    }
    if (addedSuccess) {
      setSubmitSuccess(true)
      removeQueryParam('addedSuccess')
    }
    API.get(`/governance/audits/template/${id}`)
      .then((res) => {
        if (res.status === 200) {
          setForm(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch((e) => {
        console.error(e)
        setFetchError(true)
      })
      .finally(() => setLoading(false))
  }, [])
  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const updateQuestion = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((question, i) =>
        i === index ? { ...question, [field]: value } : question,
      ),
    }))
  }

  const addQuestion = () => {
    setForm((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          name: '',
          guidance: '',
          required: true,
        },
      ],
    }))
  }

  const removeQuestion = (index) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }))
  }

  const handleSave = async () => {
    setUploading(true)
    const payload = {
      ...form,
      requiredFrequency:
        form.requiredFrequency === '' ? null : Number(form.requiredFrequency),
    }

    if (id) {
      API.patch(`/governance/audits/templates/${id}`, payload)
        .then((res) => {
          if (res.status === 200) {
            setSubmitSuccess(true)
          } else {
            setSubmitError(true)
          }
        })
        .catch(() => setSubmitError(true))
        .finally(() => setUploading(false))
    } else {
      API.post('/governance/audits/templates/new', payload)
        .then((res) => {
          if (res.status === 200) {
            navigate(
              '/governance/audit-templates/edit/' +
                res.data +
                '?addedSuccess=true',
            )
          } else {
            setSubmitError(true)
          }
        })
        .catch(() => setSubmitError(true))
        .finally(() => setUploading(false))
    }
  }

  if (loading) {
    return <Loading />
  }
  if (fetchError) {
    return (
      <ErrorModal
        open
        statusCode="Download"
        message="Unable to fetch Audit Template"
        noAutoClose
      />
    )
  }

  return (
    <Box
      sx={{
        p: 3,
        width: '100%',
        maxWidth: 1400,
        mx: 'auto',
        boxSizing: 'border-box',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={() => navigate('/governance/audit-templates')}>
            <ArrowBack />
          </IconButton>

          <Box>
            <Typography variant="h4" fontWeight={900}>
              {id ? 'Edit' : 'Create'} Audit Template
            </Typography>
            <Typography color="text.secondary">
              Configure the audit details, schedule and checklist items.
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={uploading || noSubmit}
          sx={{
            borderRadius: 3,
            px: 3,
            py: 1.2,
            fontWeight: 700,
          }}
        >
          {`${id ? 'Save' : 'Create'} Template`}
        </Button>
      </Stack>

      {/* {form.isSystemTemplate && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
          This is a system template. Tenants can use it as standard, disable it
          if allowed, or clone it to customise their own version.
        </Alert>
      )} */}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              mb: 3,
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 3 }}
            >
              <Checklist color="primary" />
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  Template Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  These details appear when users select an audit to complete.
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Template title"
                  value={form.title}
                  autoComplete="off"
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="e.g. Infection Prevention & Control Audit"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  value={form.category}
                  onChange={(e) => updateField('category', e.target.value)}
                >
                  {categoryOptions.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Required frequency"
                  value={form.requiredFrequency ?? ''}
                  onChange={(e) =>
                    updateField('requiredFrequency', e.target.value)
                  }
                >
                  {frequencyOptions.map((option) => (
                    <MenuItem key={option.label} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Description"
                  value={form.auditDescription}
                  autoComplete="off"
                  onChange={(e) =>
                    updateField('auditDescription', e.target.value)
                  }
                  placeholder="Describe what this audit checks and when it should be used..."
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  Checklist Items
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add the checks users must complete during this audit.
                </Typography>
              </Box>

              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={addQuestion}
                sx={{ borderRadius: 3 }}
              >
                Add Item
              </Button>
            </Stack>

            <Stack spacing={2}>
              {form.questions.map((question, index) => (
                <Card
                  key={index}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                  }}
                >
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      {/* <Tooltip title="Drag to reorder later">
                        <DragIndicator
                          sx={{ color: 'text.secondary', mt: 1 }}
                        />
                      </Tooltip> */}

                      <Box sx={{ flexGrow: 1 }}>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={2}
                          alignItems={{ xs: 'stretch', md: 'center' }}
                        >
                          <TextField
                            fullWidth
                            label={`Checklist item ${index + 1}`}
                            value={question.name}
                            onChange={(e) =>
                              updateQuestion(index, 'name', e.target.value)
                            }
                            autoComplete="off"
                            placeholder="e.g. PPE is available and appropriately used"
                          />

                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!question.required}
                                onChange={(e) =>
                                  updateQuestion(
                                    index,
                                    'required',
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label="Required"
                            sx={{ minWidth: 130 }}
                          />

                          <IconButton
                            color="error"
                            disabled={form.questions.length === 1}
                            onClick={() => removeQuestion(index)}
                          >
                            <Delete />
                          </IconButton>
                        </Stack>

                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label="Guidance"
                          value={question.guidance}
                          onChange={(e) =>
                            updateQuestion(index, 'guidance', e.target.value)
                          }
                          autoComplete="off"
                          placeholder="Add guidance to help users assess this item consistently..."
                          sx={{ mt: 2 }}
                        />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack
            spacing={3}
            sx={{
              position: { xs: 'static', lg: 'sticky' },
              top: 24,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Shield color="primary" />
                <Typography variant="h6" fontWeight={800}>
                  Template Settings
                </Typography>
              </Stack>

              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.isActive}
                      onChange={(e) =>
                        updateField('isActive', e.target.checked)
                      }
                    />
                  }
                  label="Active"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.isRequired}
                      onChange={(e) =>
                        updateField('isRequired', e.target.checked)
                      }
                    />
                  }
                  label="Required"
                />
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <EventRepeat color="primary" />
                <Typography variant="h6" fontWeight={800}>
                  Preview
                </Typography>
              </Stack>

              <Stack spacing={1.5}>
                <Typography variant="h6" fontWeight={800}>
                  {form.title || 'Untitled Audit Template'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {form.auditDescription || 'No description added yet.'}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {form.category && (
                    <Chip label={form.category} size="small" color="contrast" />
                  )}
                  <Chip
                    size="small"
                    label={
                      form.requiredFrequency
                        ? frequencyOptions.find(
                            (o) => o.value === form.requiredFrequency,
                          ).label
                        : 'Ad hoc'
                    }
                    color="contrast"
                  />
                  <Chip
                    size="small"
                    variant={form.isRequired ? 'filled' : 'outlined'}
                    color={form.isRequired ? 'success' : 'error'}
                    label={form.isRequired ? 'Required' : 'Not required'}
                  />
                  <Chip
                    size="small"
                    variant={form.isActive ? 'filled' : 'outlined'}
                    color={form.isActive ? 'success' : 'error'}
                    label={form.isActive ? 'Active' : 'Inactive'}
                  />
                </Stack>

                <Divider />

                <Typography variant="body2" color="text.secondary">
                  {form.questions.length} checklist item
                  {form.questions.length === 1 ? '' : 's'}
                </Typography>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
      <ErrorModal
        open={submitError || submitSuccess}
        onClose={() => {
          setSubmitError(false)
          setSubmitSuccess(false)
        }}
        message={
          submitSuccess
            ? `Template ${id && !addedSuccess ? 'Updated' : 'Created'}`
            : `Unable to ${id ? 'Edit' : 'Create'} Template`
        }
        success={submitSuccess}
      />
    </Box>
  )
}
